/**
 * Formatos dos registros do pré-natal (Módulo 4): testes, exames, tratamento, doses, busca ativa,
 * consultas e "Novo" (D-028). Os mesmos schemas validam o formulário e a API.
 */
import { z } from 'zod';
import { ESTAGIOS_SIFILIS } from '../protocolo/regras';
import { hojeEmBrasilia } from './esquemas-cadastro';

export const PESSOAS_PRENATAL = ['gestante', 'parceiro'] as const;
export type PessoaPrenatal = (typeof PESSOAS_PRENATAL)[number];

/** Testes rápidos do pré-natal. Hepatites ficam "Em desenvolvimento" (D-014) conforme o parâmetro do sistema. */
export const TESTES_RAPIDOS = ['sifilis', 'hiv', 'hbv', 'hcv'] as const;
export const ROTULO_TESTE_RAPIDO: Record<(typeof TESTES_RAPIDOS)[number], string> = {
  sifilis: 'Sífilis',
  hiv: 'HIV',
  hbv: 'Hepatite B',
  hcv: 'Hepatite C',
};

export const EXAMES_NAO_TREPONEMICOS = ['vdrl', 'rpr'] as const;
export const EXAMES_TREPONEMICOS = ['teste_treponemico', 'ftabs', 'tpha'] as const;
export const ROTULO_EXAME: Record<string, string> = {
  vdrl: 'VDRL',
  rpr: 'RPR',
  teste_treponemico: 'Teste treponêmico',
  ftabs: 'FTA-Abs',
  tpha: 'TPHA',
  ...ROTULO_TESTE_RAPIDO,
};

export const RESULTADOS_TESTE = [
  'reagente',
  'nao_reagente',
  'indeterminado',
  'nao_realizado',
] as const;
export const RESULTADOS_LABORATORIO = ['reagente', 'nao_reagente', 'aguardando_resultado'] as const;
export const ROTULO_RESULTADO: Record<string, string> = {
  reagente: 'Reagente',
  nao_reagente: 'Não reagente',
  indeterminado: 'Indeterminado',
  nao_realizado: 'Não realizado',
  aguardando_resultado: 'Aguardando resultado',
};

/** Títulos aceitos (enum `valor_titulacao_enum` do banco). */
export const TITULOS = [
  '1:1',
  '1:2',
  '1:4',
  '1:8',
  '1:16',
  '1:32',
  '1:64',
  '1:128',
  '1:256',
  '1:512',
  '1:1024',
] as const;

const dataPassada = (rotulo: string) =>
  z.iso
    .date({ message: 'Informe uma data válida.' })
    .refine((d) => d <= hojeEmBrasilia(), { message: `${rotulo} não pode estar no futuro.` });

const textoOpcional = (max = 2000) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const justificativa = z
  .string()
  .trim()
  .min(10, { message: 'Explique o motivo (mínimo de 10 caracteres).' })
  .max(1000);

// ---------------------------------------------------------------- testes e exames
/** Testes do pré-natal ou da admissão na maternidade (D-035). */
export const FASES_EXAME = ['pre_natal', 'maternidade'] as const;

export const testeRapidoSchema = z.object({
  pessoa: z.enum(PESSOAS_PRENATAL),
  fase: z.enum(FASES_EXAME).default('pre_natal'),
  tipoExame: z.enum(TESTES_RAPIDOS),
  dataColeta: dataPassada('A data do teste'),
  resultado: z.enum(RESULTADOS_TESTE, { message: 'Escolha o resultado.' }),
  observacoes: textoOpcional(),
});
export type DadosTesteRapido = z.infer<typeof testeRapidoSchema>;

export const exameLaboratorialSchema = z
  .object({
    pessoa: z.enum(PESSOAS_PRENATAL),
    fase: z.enum(FASES_EXAME).default('pre_natal'),
    tipoExame: z.enum([...EXAMES_NAO_TREPONEMICOS, ...EXAMES_TREPONEMICOS]),
    dataColeta: dataPassada('A data da coleta'),
    dataResultado: dataPassada('A data do resultado').nullable().optional(),
    resultado: z.enum(RESULTADOS_LABORATORIO, { message: 'Escolha o resultado.' }),
    titulo: z.enum(TITULOS).nullable().optional(),
    laboratorio: textoOpcional(255),
    observacoes: textoOpcional(),
  })
  .refine(
    (e) =>
      !(EXAMES_NAO_TREPONEMICOS as readonly string[]).includes(e.tipoExame) ||
      e.resultado !== 'reagente' ||
      Boolean(e.titulo),
    { path: ['titulo'], message: 'Informe o título (ex.: 1:8).' },
  )
  .refine(
    (e) =>
      !e.titulo ||
      (e.resultado === 'reagente' &&
        (EXAMES_NAO_TREPONEMICOS as readonly string[]).includes(e.tipoExame)),
    {
      path: ['titulo'],
      message: 'Título só se aplica a VDRL/RPR reagente.',
    },
  )
  .refine((e) => !e.dataResultado || e.dataResultado >= e.dataColeta, {
    path: ['dataResultado'],
    message: 'O resultado não pode ser anterior à coleta.',
  });
export type DadosExameLaboratorial = z.infer<typeof exameLaboratorialSchema>;

// ---------------------------------------------------------------- tratamento
export const MEDICAMENTOS = ['penicilina_g_benzatina', 'outro'] as const;

export const iniciarTratamentoSchema = z
  .object({
    pessoa: z.enum(PESSOAS_PRENATAL),
    /** Estágio clínico (D-001). No tratamento presuntivo do parceiro (D-029), não se aplica. */
    estagio: z.enum(ESTAGIOS_SIFILIS).nullable().optional(),
    presuntivo: z.boolean().optional(),
    medicamento: z.enum(MEDICAMENTOS).default('penicilina_g_benzatina'),
    medicamentoOutro: textoOpcional(255),
    observacoes: textoOpcional(),
  })
  .refine((t) => t.presuntivo || Boolean(t.estagio), {
    path: ['estagio'],
    message: 'Defina o estágio clínico antes de iniciar o tratamento.',
  })
  .refine((t) => !t.presuntivo || t.pessoa === 'parceiro', {
    path: ['presuntivo'],
    message: 'Tratamento presuntivo é só para o parceiro.',
  })
  .refine((t) => t.medicamento !== 'outro' || Boolean(t.medicamentoOutro), {
    path: ['medicamentoOutro'],
    message: 'Informe o medicamento.',
  });
export type DadosIniciarTratamento = z.infer<typeof iniciarTratamentoSchema>;

/** Corrigir o estágio de um tratamento (autor: edição; outro profissional: "Novo" — D-028). */
export const corrigirEstagioSchema = z.object({
  estagio: z.enum(ESTAGIOS_SIFILIS, { message: 'Informe o estágio clínico.' }),
  observacoes: textoOpcional(),
});

export const aplicarDoseSchema = z.object({
  dataAplicacao: dataPassada('A data da aplicação'),
  horaAplicacao: z
    .string()
    .regex(/^\d{2}:\d{2}$/, { message: 'Hora no formato 14:30.' })
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  lote: textoOpcional(40),
  observacoes: textoOpcional(),
});
export type DadosAplicarDose = z.infer<typeof aplicarDoseSchema>;

export const reiniciarTratamentoSchema = z.object({
  justificativa,
  dataReinicio: dataPassada('A data do reinício'),
});

export const TIPOS_ACAO_NAO_COMPARECIMENTO = [
  'contato_telefonico',
  'convocacao',
  'busca_ativa',
  'visita_domiciliar',
  'outro',
] as const;
export const ROTULO_ACAO: Record<(typeof TIPOS_ACAO_NAO_COMPARECIMENTO)[number], string> = {
  contato_telefonico: 'Contato telefônico',
  convocacao: 'Convocação',
  busca_ativa: 'Busca ativa',
  visita_domiciliar: 'Visita domiciliar',
  outro: 'Outra ação',
};

export const buscaAtivaSchema = z.object({
  tipoAcao: z.enum(TIPOS_ACAO_NAO_COMPARECIMENTO),
  dataAcao: dataPassada('A data da ação'),
  descricao: textoOpcional(),
  resultado: textoOpcional(),
});
export type DadosBuscaAtiva = z.infer<typeof buscaAtivaSchema>;

/** Item 44 da ficha de sífilis em gestante: por que o parceiro não foi tratado. */
export const MOTIVOS_NAO_TRATAMENTO_PARCEIRO = [
  'sem_contato_com_gestante',
  'nao_comunicado_convocado',
  'convocado_nao_compareceu',
  'convocado_recusou_tratamento',
  'sorologia_nao_reagente',
  'outro',
] as const;
export const ROTULO_MOTIVO_PARCEIRO: Record<
  (typeof MOTIVOS_NAO_TRATAMENTO_PARCEIRO)[number],
  string
> = {
  sem_contato_com_gestante: '1 – Parceiro sem contato com a gestante',
  nao_comunicado_convocado: '2 – Parceiro não comunicado/convocado',
  convocado_nao_compareceu: '3 – Convocado, não compareceu',
  convocado_recusou_tratamento: '4 – Convocado, recusou o tratamento',
  sorologia_nao_reagente: '5 – Sorologia não reagente',
  outro: '6 – Outro motivo',
};

export const motivoNaoTratamentoParceiroSchema = z
  .object({
    motivo: z.enum(MOTIVOS_NAO_TRATAMENTO_PARCEIRO, { message: 'Escolha o motivo.' }),
    outro: textoOpcional(500),
  })
  .refine((m) => m.motivo !== 'outro' || Boolean(m.outro), {
    path: ['outro'],
    message: 'Descreva o motivo.',
  });
export type DadosMotivoParceiro = z.infer<typeof motivoNaoTratamentoParceiroSchema>;

// ---------------------------------------------------------------- consulta e "Novo"
export const consultaPreNatalSchema = z.object({
  dataConsulta: dataPassada('A data da consulta'),
  queixa: textoOpcional(),
  avaliacao: textoOpcional(),
  conduta: textoOpcional(),
  parceiroPresente: z.boolean().nullable().optional(),
  observacoes: textoOpcional(),
});
export type DadosConsultaPreNatal = z.infer<typeof consultaPreNatalSchema>;

/** D-028: substituir um registro de outra pessoa ("Novo"). */
export const substituirSchema = z.object({ motivo: justificativa });
